import { useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { HeaderOptionsPopover } from "@/components/HeaderOptionsPopover";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { HelpCircle, Send, ChevronDown, MessageSquare } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import ReactMarkdown from "react-markdown";
import { soundManager } from "@/utils/sounds";
import { useTranslation } from "react-i18next";
import {AutoTranslate} from "@/lib/autoTranslate.tsx"
import { SEO } from "@/components/SEO";

/*  note, I don't reccomend leaving sets of untranslated commented out code like this, but I'm leaving it here as an example of what
you can do, please clean up your comments after you translate.

AutoTranslate(`How do I download my resume?`)
AutoTranslate(`Once you've completed building your resume with our AI assistant, click the **Download Resume** button at the bottom. Make sure you've provided all necessary information and typed **finish** to end the conversation.`)
AutoTranslate(`Can I save multiple resumes?`)
AutoTranslate(`Yes! With a **PRO** subscription, you can save unlimited resumes and access them anytime from the Saved Resumes page. Free users can only build one resume at a time.`)
AutoTranslate(`How do I upgrade to Pro?`)
AutoTranslate(`You can upgrade to **PRO** by visiting the [Pricing page](https://find-resumebuilder-ai.lovable.app/pricing) and selecting either the monthly ($10/month) or yearly ($90/year) plan.`)
AutoTranslate(`What file formats are supported for download?`)
AutoTranslate(`Currently, we support PDF and DOCX downloads.`)
AutoTranslate(`Can I cancel my Pro subscription anytime?`)
AutoTranslate(`Yes, you can cancel your **PRO** subscription at any time from the **Settings** page under the **Billing** section. You'll continue to have PRO access until the end of your billing period.`)
AutoTranslate(`How does the AI resume builder work?`)
AutoTranslate(`Our AI assistant guides you through a conversation to collect your professional information, then helps structure it into a professional resume format. Just answer the questions simply in your own way and the AI will do the rest.`)
AutoTranslate(`Is my personal information secure?`)
AutoTranslate(`Yes, we take **data security** seriously. All your information is encrypted and stored securely. We never share your personal data with third parties.`)
AutoTranslate(`What if I have issues and need help with the app?`)
AutoTranslate(`If you need additional help, you can contact our support team through the form on this page.`)


*/


const faqData = [
  {
    question: "How do I download my resume?",
    answer: "Once you've completed building your resume with our AI assistant, click the **Download Resume** button at the bottom. Make sure you've provided all necessary information and typed **finish** to end the conversation."
  },
  {
    question: "Can I save multiple resumes?",
    answer: "Yes! With a **PRO** subscription, you can save unlimited resumes and access them anytime from the Saved Resumes page. Free users can only build one resume at a time."
  },
  {
    question: "How do I upgrade to Pro?",
    answer: "You can upgrade to **PRO** by visiting the [Pricing page](https://find-resumebuilder-ai.lovable.app/pricing) and selecting either the monthly ($10/month) or yearly ($90/year) plan."
  },
  {
    question: "What file formats are supported for download?",
    answer: "Currently, we support PDF and DOCX downloads."
  },
  {
    question: "Can I cancel my Pro subscription anytime?",
    answer: "Yes, you can cancel your **PRO** subscription at any time from the **Settings** page under the **Billing** section. You'll continue to have PRO access until the end of your billing period."
  },
  {
    question: "How does the AI resume builder work?",
    answer: "Our AI assistant guides you through a conversation to collect your professional information, then helps structure it into a professional resume format. Just answer the questions simply in your own way and the AI will do the rest."
  },
  {
    question: "Is my personal information secure?",
    answer: "Yes, we take **data security** seriously. All your information is encrypted and stored securely. We never share your personal data with third parties."
  },
  {
    question: "Can I edit my resume after it's created?",
    answer: "Yes! But this only applies to **PRO** users. As a Pro user you can rename your file, edit sections or delete your resume."
  },
  {
    question: "What if I have issues and need help with the app?",
    answer: "If you need additional help, you can contact our support team through the form on this page."
  },
];

export default function HelpPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [subject, setSubject] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [openFaqs, setOpenFaqs] = useState<number[]>([]);
  const autoTranslateText = t('autoTranslate', { returnObjects: true });

  // Reactive translation variables
  const helpSupport = autoTranslateText["Help & Support"] || AutoTranslate("Help & Support");
  const contactSupport = autoTranslateText["Contact Support"] || AutoTranslate("Contact Support");
  const havingIssues = autoTranslateText["Having issues? Send us a message and we'll help you out."] || AutoTranslate("Having issues? Send us a message and we'll help you out.");
  const subjectLabel = autoTranslateText["Subject"] || AutoTranslate("Subject");
  const describeIssue = autoTranslateText["Describe your issue"] || AutoTranslate("Describe your issue");
  const pleaseDescribe = autoTranslateText["Please describe your issue in detail..."] || AutoTranslate("Please describe your issue in detail...");
  const sending = autoTranslateText["Sending..."] || AutoTranslate("Sending...");
  const submit = autoTranslateText["Submit"] || AutoTranslate("Submit");
  const cancel = autoTranslateText["Cancel"] || AutoTranslate("Cancel");
  const frequentlyAsked = autoTranslateText["Frequently Asked Questions"] || AutoTranslate("Frequently Asked Questions");
  const findQuick = autoTranslateText["Find quick answers to your questions"] || AutoTranslate("Find quick answers to your questions");
  const selectTopic = AutoTranslate("Select a topic");

  const subjectOptions = [
    "Can't download my resume",
    "Can't upgrade/downgrade",
    "Feedback on a feature",
    "Resume Builder bot errors",
    "Other"
  ];

  const toggleFaq = (index: number) => {
    setOpenFaqs(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleSubmit = async () => {
    const finalSubject = subject === "Other" ? customSubject : subject;

    if (!finalSubject || !message.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all fields before submitting.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          subject: finalSubject,
          message: message,
          userEmail: user?.email,
          userName: user?.user_metadata?.full_name
        }
      });

      if (error) throw error;

      soundManager.messageSent();
      toast({
        title: "Message sent successfully!",
        description: "We've received your message and will get back to you soon.",
      });

      // Reset form
      setSubject("");
      setCustomSubject("");
      setMessage("");
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    }

    setLoading(false);
  };

  return (
    <SidebarProvider>
      <SEO
        title="Help & Support — FIND Resume Builder"
        description="Get answers about FIND Resume Builder. Browse FAQs on AI resume generation, templates, exports, subscriptions, and contact our support team."
        path="/help"
      />
      <div className="min-h-screen flex w-full bg-gradient-main">
        <AppSidebar />

        <main className="flex-1 flex flex-col relative z-10">
          <header className="flex items-center justify-between p-4 border-b border-border bg-card/80 backdrop-blur-sm">
            <div className="flex items-center gap-4">
              <SidebarTrigger className="hover:bg-primary-light" />
              <div className="flex items-center gap-2">
                <HelpCircle className="h-6 w-6 text-primary" />
                <h1 className="text-xl font-bold text-foreground">{helpSupport}</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <HeaderOptionsPopover />
            </div>
          </header>

          <div className="flex-1 p-6 space-y-8 max-w-4xl mx-auto w-full">
            {/* Support Section */}
            <Card className="bg-card/80 backdrop-blur-sm border-card-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-primary" />
                  {contactSupport}
                </CardTitle>
                <CardDescription>
                  {havingIssues}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{subjectLabel}</label>
                  <Select value={subject} onValueChange={setSubject}>
                    <SelectTrigger>
                      <SelectValue placeholder={selectTopic} />
                    </SelectTrigger>
                    <SelectContent>
                      {subjectOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {AutoTranslate(option)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {subject === "Other" && (
                    <input
                      type="text"
                      placeholder={AutoTranslate("Enter your subject")}
                      value={customSubject}
                      onChange={(e) => setCustomSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-input rounded-md bg-background"
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">{describeIssue}</label>
                  <Textarea
                    placeholder={pleaseDescribe}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex-1"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {loading ? sending : submit}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSubject("");
                      setCustomSubject("");
                      setMessage("");
                    }}
                    className="flex-1"
                  >
                    {cancel}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* FAQ Section */}
            <Card className="bg-card/80 backdrop-blur-sm border-card-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-primary" />
                  {frequentlyAsked}
                </CardTitle>
                <CardDescription>
                  {findQuick}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {faqData.map((faq, index) => (
                    <Collapsible key={index}>
                      <CollapsibleTrigger
                        onClick={() => toggleFaq(index)}
                        className="flex items-center justify-between w-full p-4 text-left bg-muted/50 hover:bg-muted/70 rounded-lg transition-colors"
                      >
                        <span className="font-medium">{autoTranslateText[faq.question.replace(/ /g, "").replace(/:/g, "▌").replace(/\./g, "⌡")] || AutoTranslate(faq.question)}</span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${
                            openFaqs.includes(index) ? 'rotate-180' : ''
                          }`}
                        />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="px-4 py-3 text-muted-foreground">
                        <div className="prose prose-sm max-w-none">
                          <ReactMarkdown
                            components={{
                              a: ({ href, children, ...props }) => (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary underline hover:text-primary/80"
                                  {...props}
                                >
                                  {children}
                                </a>
                              ),
                              strong: ({ children }) => (
                                <strong className="font-semibold text-foreground">{children}</strong>
                              ),
                              p: ({ children }) => (
                                <p className="mb-2 last:mb-0">{children}</p>
                              )
                            }}
                          >
                            {autoTranslateText[faq.answer.replace(/ /g, "").replace(/:/g, "▌").replace(/\./g, "⌡")] || AutoTranslate(faq.answer)}
                          </ReactMarkdown>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <Footer />
        </main>
      </div>
    </SidebarProvider>
  );
}
